import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AIChatbot from '../components/AIChatbot';
import './CategoryPage.css';

function CategoryPage() {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChatbot, setShowChatbot] = useState(false);
  const [stats, setStats] = useState({
    freelancers: 12,
    avgPrice: 45,
    avgTime: 14,
    satisfaction: 98
  });

  useEffect(() => {
    loadCategoryData();
  }, [categoryName]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      
      // Load category
      const categoriesResponse = await axios.get('http://localhost:5001/api/categories');
      const foundCategory = categoriesResponse.data.find(
        cat => cat.name === decodeURIComponent(categoryName) || 
               cat.nameEn === decodeURIComponent(categoryName)
      );

      if (!foundCategory) {
        navigate('/');
        return;
      }

      setCategory(foundCategory);

      // Load services for this category
      const servicesResponse = await axios.get('http://localhost:5001/api/services');
      const categoryServices = servicesResponse.data.filter(
        service => service.category === foundCategory.name
      );
      setServices(categoryServices);

      setLoading(false);
    } catch (error) {
      console.error('Error loading category:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Φόρτωση...</p>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="error-container">
        <h2>Η κατηγορία δεν βρέθηκε</h2>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Επιστροφή στην Αρχική
        </button>
      </div>
    );
  }

  return (
    <div className="category-page">
      {/* Hero Section */}
      <section className="category-hero">
        <div className="category-hero-content">
          <div className="category-icon-large">{category.icon}</div>
          <h1 className="category-title">{category.name}</h1>
          <p className="category-description-large">{category.description}</p>
          <div className="category-actions-hero">
            <button 
              className="btn btn-primary btn-large"
              onClick={() => {/* Navigate to request form */}}
            >
              Ζητήστε Προσφορά
            </button>
            <button 
              className="btn btn-ai-large"
              onClick={() => setShowChatbot(true)}
            >
              🤖 Μιλήστε με AI Βοηθό
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="category-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-value">{stats.freelancers}+</div>
              <div className="stat-label">Ενεργοί Freelancers</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-value">€{stats.avgPrice}/ώρα</div>
              <div className="stat-label">Μέση Τιμή</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏱️</div>
              <div className="stat-value">{stats.avgTime} μέρες</div>
              <div className="stat-label">Μέσος Χρόνος Παράδοσης</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-value">{stats.satisfaction}%</div>
              <div className="stat-label">Ικανοποίηση Πελατών</div>
            </div>
          </div>
        </div>
      </section>

      {/* Analysis Section */}
      <section className="category-analysis">
        <div className="container">
          <h2 className="section-title">Ανάλυση Κατηγορίας</h2>
          
          <div className="analysis-grid">
            {/* Popular Technologies */}
            <div className="analysis-card">
              <h3>🔧 Δημοφιλείς Τεχνολογίες</h3>
              <div className="tech-tags">
                {getTechnologiesForCategory(category.name).map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>

            {/* Typical Projects */}
            <div className="analysis-card">
              <h3>📊 Τυπικά Έργα</h3>
              <ul className="project-types-list">
                {getProjectTypesForCategory(category.name).map((project, index) => (
                  <li key={index}>
                    <span className="project-icon">✓</span>
                    {project}
                  </li>
                ))}
              </ul>
            </div>

            {/* Price Range */}
            <div className="analysis-card">
              <h3>💵 Εύρος Τιμών</h3>
              <div className="price-range">
                <div className="price-item">
                  <span className="price-label">Βασικό Έργο</span>
                  <span className="price-value">€500 - €2,000</span>
                </div>
                <div className="price-item">
                  <span className="price-label">Μεσαίο Έργο</span>
                  <span className="price-value">€2,000 - €8,000</span>
                </div>
                <div className="price-item">
                  <span className="price-label">Σύνθετο Έργο</span>
                  <span className="price-value">€8,000+</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="analysis-card">
              <h3>⏰ Χρονοδιάγραμμα</h3>
              <div className="timeline-info">
                <div className="timeline-item">
                  <span className="timeline-icon">🚀</span>
                  <div>
                    <strong>Γρήγορη Ανάπτυξη</strong>
                    <p>1-2 εβδομάδες για απλά projects</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <span className="timeline-icon">🏗️</span>
                  <div>
                    <strong>Κανονική Ανάπτυξη</strong>
                    <p>1-3 μήνες για μεσαία projects</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <span className="timeline-icon">🏛️</span>
                  <div>
                    <strong>Enterprise</strong>
                    <p>3+ μήνες για σύνθετα projects</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      {services.length > 0 && (
        <section className="category-services">
          <div className="container">
            <h2 className="section-title">Διαθέσιμες Υπηρεσίες</h2>
            <div className="services-grid">
              {services.map(service => (
                <div key={service.id} className="service-card">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  {service.price && (
                    <div className="service-price">
                      Από €{service.price}
                    </div>
                  )}
                  <button className="btn btn-outline">
                    Περισσότερα
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="category-faq">
        <div className="container">
          <h2 className="section-title">Συχνές Ερωτήσεις</h2>
          <div className="faq-grid">
            {getFAQForCategory(category.name).map((faq, index) => (
              <div key={index} className="faq-card">
                <h4>{faq.question}</h4>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
          <div className="faq-ai-prompt">
            <p>Έχετε περισσότερες ερωτήσεις;</p>
            <button 
              className="btn btn-ai"
              onClick={() => setShowChatbot(true)}
            >
              🤖 Ρωτήστε το AI
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="category-cta">
        <div className="container">
          <h2>Ξεκινήστε το Έργο σας Σήμερα</h2>
          <p>Βρείτε τον ιδανικό freelancer για {category.name}</p>
          <button className="btn btn-primary btn-large">
            Ζητήστε Δωρεάν Προσφορά
          </button>
        </div>
      </section>

      {/* Floating AI Button */}
      {!showChatbot && (
        <button 
          className="floating-ai-btn"
          onClick={() => setShowChatbot(true)}
        >
          <span className="floating-ai-icon">🤖</span>
          <span className="floating-ai-text">Βοήθεια AI</span>
        </button>
      )}

      {/* AI Chatbot */}
      {showChatbot && (
        <AIChatbot 
          category={category.name}
          onClose={() => setShowChatbot(false)}
        />
      )}
    </div>
  );
}

// Helper functions for category-specific content
function getTechnologiesForCategory(categoryName) {
  const techMap = {
    'Ανάπτυξη Ιστοσελίδων': ['React', 'Vue.js', 'Node.js', 'WordPress', 'Laravel', 'HTML5/CSS3'],
    'Ανάπτυξη Mobile Apps': ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Ionic'],
    'UI/UX Design': ['Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator'],
    'Backend Development': ['Node.js', 'Python', 'Java', 'PostgreSQL', 'MongoDB', 'Redis'],
    'DevOps': ['Docker', 'Kubernetes', 'AWS', 'Jenkins', 'Terraform', 'GitLab CI'],
    'Data Science': ['Python', 'TensorFlow', 'PyTorch', 'SQL', 'Jupyter', 'Tableau']
  };
  return techMap[categoryName] || ['Technology 1', 'Technology 2', 'Technology 3'];
}

function getProjectTypesForCategory(categoryName) {
  const projectMap = {
    'Ανάπτυξη Ιστοσελίδων': [
      'E-commerce πλατφόρμες',
      'Corporate websites',
      'Landing pages',
      'Web applications',
      'CMS customization'
    ],
    'Ανάπτυξη Mobile Apps': [
      'iOS εφαρμογές',
      'Android εφαρμογές',
      'Cross-platform apps',
      'MVP development',
      'App redesign'
    ],
    'UI/UX Design': [
      'Website design',
      'Mobile app design',
      'User research',
      'Wireframing',
      'Prototyping'
    ],
    'Backend Development': [
      'REST APIs',
      'Microservices',
      'Database design',
      'Server setup',
      'API integration'
    ],
    'DevOps': [
      'CI/CD pipelines',
      'Cloud migration',
      'Infrastructure setup',
      'Monitoring',
      'Security hardening'
    ],
    'Data Science': [
      'Data analysis',
      'Machine learning models',
      'Data visualization',
      'Predictive analytics',
      'ETL pipelines'
    ]
  };
  return projectMap[categoryName] || ['Project Type 1', 'Project Type 2', 'Project Type 3'];
}

function getFAQForCategory(categoryName) {
  return [
    {
      question: 'Πόσο κοστίζει ένα τυπικό έργο;',
      answer: 'Το κόστος εξαρτάται από την πολυπλοκότητα. Βασικά έργα ξεκινούν από €500, ενώ πιο σύνθετα μπορούν να φτάσουν τα €10,000+.'
    },
    {
      question: 'Πόσος χρόνος χρειάζεται;',
      answer: 'Ο μέσος χρόνος παράδοσης είναι 2-4 εβδομάδες για απλά έργα. Μεγαλύτερα projects μπορεί να διαρκέσουν 2-6 μήνες.'
    },
    {
      question: 'Τι περιλαμβάνει η υπηρεσία;',
      answer: 'Περιλαμβάνει σχεδιασμό, ανάπτυξη, testing, και υποστήριξη μετά την παράδοση του έργου.'
    },
    {
      question: 'Πώς επιλέγω τον κατάλληλο freelancer;',
      answer: 'Μπορείτε να δείτε το portfolio, τις αξιολογήσεις, και να κάνετε interview με τους υποψήφιους freelancers.'
    }
  ];
}

export default CategoryPage;
